import {
  makeStyles,
  createStyles,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Theme,
} from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useCleverlynkContext } from "../../contexts/CleverlynkContext";
import useCleverlynkFunctions from "../../hooks/useCleverlynkFunctions";
import useLandingFunctions from "../../hooks/useLandingFunctions";
import useTemplateStyles from "../../hooks/useTemplateStyles";
import {
  selectActiveGroupMembers,
  selectCompany,
  selectTriggerNavToUserForm,
  selectCleverlynk,
  selectCleverlynkGroup,
} from "../../redux/slices/cleverlynkSlice";
import { selectDeliveryInfo, selectSelectedAddress } from "../../redux/slices/mapServiceSlice";
import { selectCatalog } from "../../redux/slices/templateSlice";

interface IGroupDrawerSection {
  onClose: () => void;
  collapse: boolean;
}

const GroupDrawerSection = ({ onClose, collapse }: IGroupDrawerSection) => {
  const { handleRedirectToClynk, handleModifyOpenGeo } = useCleverlynkFunctions();

  const activeGroupMembers = useSelector(selectActiveGroupMembers);
  const navigateToForm = useSelector(selectTriggerNavToUserForm);
  const cleverlynk = useSelector(selectCleverlynk);
  const cleverlynkGroup = useSelector(selectCleverlynkGroup);
  const selectedAddress = useSelector(selectSelectedAddress);
  const deliveryInfo = useSelector(selectDeliveryInfo);

  const handleClynkIdOnConfirm = (id: string) => {
    onClose();
    handleRedirectToClynk({
      id,
      navigateToForm,
    });
  };

  const handleOpenGeo = () => {
    onClose();
    handleModifyOpenGeo(true);
  };

  const { t } = useTranslation("groups");
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      drawerPaper: {
        width: "85%",
        // backgroundColor: navbarStyles?.navbarColor,
        // color: navbarStyles?.navbarTextColor,
        color: "#1a1a1a",
        position: "relative",
        overflowX: "hidden",
      },
      nestedItem: {
        padding: 0,
        paddingLeft: 45,
      },
      primaryCategoryText: {
        fontSize: "1.15rem",
        fontWeight: "bold",
      },
      primarySubCategoryText: {
        fontSize: "1rem",
        fontWeight: "bold",
      },
      mainOptionText: {
        fontWeight: "bold",
        color: "#1a1a1a",
        fontSize: "1.45rem",
      },
      companyName: {
        fontSize: "1.2rem",
      },
      topSvg: {
        top: 0,
        right: -1,
        position: "absolute",
        maxHeight: "40%",
        zIndex: -1,
        [theme.breakpoints.up("md")]: {
          width: 378,
        },
        maxWidth: 378,
      },
      stickToBottom: {
        // position: "fixed",
        // bottom: 0,
        // left: 0,
        flexGrow: 3,
      },
      upperGroupDiv: {
        minHeight: collapse ? 80 : 100,
        transition: "min-height 400ms",
        backgroundColor: "white",
        color: "#1a1a1a",
        borderTop: "solid 2pt #e7e7e7",
      },
      fillAvailableHeight: {
        height: "-moz-available" /* WebKit-based browsers will ignore this. */,
        //@ts-ignore
        height: "-webkit-fill-available" /* Mozilla-based browsers will ignore this. */,
        //@ts-ignore
        height: "fill-available",
      },
      lowerDiv: {
        height: "-moz-available" /* WebKit-based browsers will ignore this. */,
        //@ts-ignore
        height: "-webkit-fill-available" /* Mozilla-based browsers will ignore this. */,
        //@ts-ignore
        height: "fill-available",
        color: "white",
        backgroundColor: "#333333",
        transition: "min-height 400ms",
        minHeight: collapse ? 100 : 140,
        maxHeight: window.innerHeight / 2,
        paddingTop: 20,
        "&.MuiOutlinedInput-notchedOutline": {
          borderColor: "white",
        },
      },
      selectForm: {
        width: "80%",
        margin: 8,
      },
      select: {
        color: "white",
        borderColor: "white",
      },
      selectIcon: {
        color: "#ffffff8a",
      },
      selectRoot: {
        borderColor: "white",
      },
      selectOutlined: {
        color: "white",
      },
      listItem: {
        margin: 8,
        boxShadow: "1pt 1pt 2px 1px #ccc8c87d",
        borderRadius: 5,
        backgroundColor: "#f9f9f9",
        color: "#1a1a1a",
        "&:hover": {
          backgroundColor: "#ccc",
          color: "#1a1a1a",
        },
      },
    })
  );
  const classes = useStyles();

  const select = useMemo(
    () => (
      <Grid item container direction="column" id="select-lowerdiv" className={classes.lowerDiv}>
        <Grid item>
          <Typography align="center">{t("groupPopup.whereToGo")}</Typography>
        </Grid>
        <Grid item container justifyContent="center" style={{ paddingTop: 10 }}>
          {activeGroupMembers.length > 2 ? (
            <FormControl required variant="outlined" className={classes.selectForm}>
              <InputLabel style={{ color: "white" }} id="select-selectedId-label">
                {t("groupPopup.choose")}
              </InputLabel>
              <Select
                classes={{
                  outlined: classes.selectOutlined,
                  root: classes.selectRoot,
                  icon: classes.selectIcon,
                }}
                variant="outlined"
                labelId="select-selectedId-label"
                onChange={event => handleClynkIdOnConfirm(event.target.value as string)}>
                {activeGroupMembers
                  ?.filter(c => c.id !== cleverlynk?.id)
                  .map((clynkInfo, index) => (
                    <MenuItem key={`selectedId-${index}`} value={clynkInfo.id} disabled={!clynkInfo.open}>
                      {clynkInfo.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          ) : (
            <Button
              className={classes.listItem}
              disabled={!activeGroupMembers.filter(c => c.id !== cleverlynk?.id)[0]?.open}
              onClick={() =>
                handleClynkIdOnConfirm(activeGroupMembers.filter(c => c.id !== cleverlynk?.id)[0]?.id ?? "")
              }>
              {activeGroupMembers.filter(c => c.id !== cleverlynk?.id)[0]?.name}
            </Button>
          )}
        </Grid>
      </Grid>
    ),
    [
      activeGroupMembers,
      classes.listItem,
      classes.lowerDiv,
      classes.selectForm,
      classes.selectIcon,
      classes.selectOutlined,
      classes.selectRoot,
      cleverlynk?.id,
    ]
  );

  const geo = useMemo(
    () => (
      <Grid
        item
        container
        direction="column"
        id="geo-lowerdiv"
        className={classes.lowerDiv}
        justifyContent="flex-start"
        style={{ width: "100%", margin: 0 }}
        alignContent="center">
        {selectedAddress ? (
          <React.Fragment>
            <Grid item style={{ padding: collapse ? 4 : 12 }}>
              <Typography align="center">{t("groupPopup.address")}</Typography>
            </Grid>
            <Grid item container direction="column" alignItems="center">
              <Grid item>
                <Typography align="center">{deliveryInfo.address}</Typography>
              </Grid>
              <Grid item>
                <Button
                  onClick={handleOpenGeo}
                  style={{
                    textTransform: "none",
                    color: "white",
                    fontSize: "0.75rem",
                    textDecoration: "underline",
                    paddingTop: 0,
                  }}>
                  {t("groupPopup.change")}
                </Button>
              </Grid>
            </Grid>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Grid item style={{ padding: collapse ? 4 : 12 }}>
              <Typography style={{ fontSize: "0.85rem" }} align="center">
                {t("groupPopup.noAddress")}
              </Typography>
            </Grid>
            <Grid item container justifyContent="space-evenly" alignContent="center">
              <Button style={{ paddingTop: 10, paddingBottom: 10 }} onClick={handleOpenGeo}>
                <Typography variant="overline" style={{ color: "white", textDecoration: "underline" }}>
                  {t("groupPopup.addAddress")}
                </Typography>
              </Button>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
    ),
    [classes.lowerDiv, deliveryInfo.address, selectedAddress]
  );

  const content = useMemo(() => {
    switch (cleverlynkGroup?.type) {
      case "GEO":
        return geo;
      default:
        return select;
    }
  }, [cleverlynkGroup?.type, geo, select]);

  return !!cleverlynkGroup?.id ? (
    <Grid container direction="column" justifyContent="flex-start" className={classes.stickToBottom} wrap="nowrap">
      <Grid
        item
        container
        direction="column"
        className={classes.upperGroupDiv}
        justifyContent="space-evenly"
        alignContent="center">
        <Grid item>
          <Typography style={{ fontWeight: "bold" }} align="center">
            {t("groupPopup.youAreIn")}
          </Typography>
        </Grid>
        <Grid item>
          <Typography align="center">{cleverlynk?.name}</Typography>
        </Grid>
      </Grid>
      {content}
    </Grid>
  ) : null;
};
export default GroupDrawerSection;
